/**
 * Sky dome, sun/moon, day/night cycle and fog for the empty test world.
 * Shader and colour maths ported from reference-prototype/src/sky.js; the clock
 * maths lives in @kota-nova/sim (pure) and the weather in the WeatherSystem below.
 */
import type {
  AbstractEngine,
  DirectionalLight,
  HemisphericLight,
  Scene,
  ShaderMaterial,
  Mesh,
} from 'babylonjs';
import type { ClockSync, WeatherParams } from '@kota-nova/shared';
import { WEATHER_PRESETS, type WeatherState } from '@kota-nova/shared';
import { dayMinutes, sunDirection, worldDay, weatherAtWorldTime, worldMinutes } from '@kota-nova/sim';

const SKY_VERT = `
precision highp float;
attribute vec3 position;
uniform mat4 worldViewProjection;
varying vec3 vDir;
void main(void){ vDir = position; vec4 p = worldViewProjection * vec4(position, 1.0); gl_Position = p.xyww; gl_Position.z = p.w * 0.99999; }`;

const SKY_FRAG = `
precision highp float;
varying vec3 vDir;
uniform vec3 sunDir; uniform float time; uniform float cloud; uniform float overcast; uniform float flash; uniform float fogAmt; uniform vec3 fogColor; uniform float octaves;
float hash(vec3 p){ p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float noise(vec3 x){ vec3 i = floor(x); vec3 f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(hash(i), hash(i + vec3(1,0,0)), f.x), mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y),
             mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x), mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y), f.z); }
float fbm(vec3 p){ float a = 0.5, s = 0.0; for (int i = 0; i < 5; i++){ if (float(i) >= octaves) break; s += a * noise(p); p *= 2.03; a *= 0.5; } return s; }
void main(void){
  vec3 d = normalize(vDir);
  float sunH = sunDir.y;
  float dayW = smoothstep(-0.06, 0.28, sunH);
  float duskW = clamp(1.0 - abs(sunH - 0.02) * 6.0, 0.0, 1.0);
  vec3 dayZen = vec3(0.13, 0.33, 0.70), dayHor = vec3(0.60, 0.75, 0.90);
  vec3 nightZen = vec3(0.008, 0.014, 0.04), nightHor = vec3(0.05, 0.065, 0.11);
  vec3 duskHor = vec3(1.0, 0.56, 0.32), duskZen = vec3(0.16, 0.22, 0.48);
  float h = max(d.y, 0.0);
  vec3 zen = mix(nightZen, dayZen, dayW); vec3 hor = mix(nightHor, dayHor, dayW);
  zen = mix(zen, duskZen, duskW * 0.55);
  vec2 dh = normalize(d.xz + 1e-4); vec2 sh = normalize(sunDir.xz + 1e-4);
  float toward = max(dot(dh, sh), 0.0);
  hor = mix(hor, duskHor, duskW * (0.25 + 0.75 * pow(toward, 3.0)));
  vec3 col = mix(zen, hor, pow(1.0 - h, 3.5));
  col += vec3(0.16, 0.09, 0.05) * (1.0 - dayW) * pow(1.0 - h, 8.0);
  float sd = max(dot(d, sunDir), 0.0);
  col += vec3(1.0, 0.82, 0.55) * pow(sd, 900.0) * 30.0 * step(-0.02, sunH) * (1.0 - overcast);
  col += vec3(1.0, 0.65, 0.35) * pow(sd, 10.0) * 0.35 * (dayW * 0.6 + duskW) * (1.0 - overcast * 0.8);
  vec3 moonDir = normalize(vec3(-sunDir.x, abs(sunDir.y) + 0.25, -sunDir.z));
  float md = dot(d, moonDir);
  col += vec3(0.85, 0.88, 1.0) * smoothstep(0.99955, 0.99975, md) * (1.0 - dayW) * (1.0 - overcast) * 1.6;
  col += vec3(0.3, 0.35, 0.5) * pow(max(md, 0.0), 60.0) * (1.0 - dayW) * 0.25;
  float st = hash(floor(d * 380.0));
  col += vec3(0.9) * step(0.9972, st) * (1.0 - dayW) * (1.0 - cloud) * smoothstep(0.05, 0.4, h);
  if (d.y > 0.0) {
    vec2 uv = d.xz / (d.y + 0.12);
    float n = fbm(vec3(uv * 1.1 + vec2(time * 0.006, time * 0.0025), time * 0.002));
    float c = smoothstep(1.02 - cloud, 1.30 - cloud * 0.55, n + cloud * 0.25) * smoothstep(0.0, 0.18, d.y);
    vec3 cTop = mix(vec3(0.05, 0.06, 0.09), vec3(1.0, 0.99, 0.97), dayW);
    vec3 cBot = mix(vec3(0.03, 0.035, 0.05), vec3(0.62, 0.65, 0.70), dayW);
    vec3 cc = mix(cBot, cTop, smoothstep(0.35, 0.9, n));
    cc = mix(cc, duskHor * 0.95, duskW * 0.55 * (0.4 + 0.6 * toward));
    cc *= (1.0 - overcast * 0.45);
    col = mix(col, cc, c * 0.96);
  }
  vec3 grey = vec3(dot(col, vec3(0.3, 0.5, 0.2)));
  // overcast grey must stay MID (see fog comment in update()): a 0.95 lift
  // at noon blew the whole sky dome + ambient to white (owner: "so white for everything")
  col = mix(col, grey * mix(0.35, 0.72, dayW), overcast * 0.75);
  col += flash * vec3(0.75, 0.8, 1.0);
  col = mix(col, fogColor, clamp(smoothstep(0.16, -0.02, d.y) * (0.55 + fogAmt * 0.45) + fogAmt * 0.35 * (1.0 - h), 0.0, 1.0));
  gl_FragColor = vec4(col, 1.0);
}`;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
type RGB = readonly [number, number, number];
const mixC = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

export interface SkyOptions {
  cloudOctaves: number;
  worldSeed: number;
  /** null = auto weather from the deterministic timeline */
  weatherOverride: WeatherState | null;
  /** IBL cube size per tier (prototype: low 32 / mid 64 / high 128) */
  envSize?: number;
}

interface BModule {
  MeshBuilder: typeof import('babylonjs').MeshBuilder;
  ShaderMaterial: typeof import('babylonjs').ShaderMaterial;
  DirectionalLight: new (n: string, d: import('babylonjs').Vector3, s: Scene) => DirectionalLight;
  HemisphericLight: new (n: string, d: import('babylonjs').Vector3, s: Scene) => HemisphericLight;
  Effect: typeof import('babylonjs').Effect;
  Vector3: typeof import('babylonjs').Vector3;
  Color3: typeof import('babylonjs').Color3;
  Scene: typeof import('babylonjs').Scene;
  ReflectionProbe: typeof import('babylonjs').ReflectionProbe;
  RenderTargetTexture: { REFRESHRATE_RENDER_ONCE: number };
}

export class SkySystem {
  readonly sun: DirectionalLight;
  readonly hemi: HemisphericLight;
  readonly skyMesh: Mesh;
  private readonly sm: ShaderMaterial;
  private readonly B: BModule;
  private readonly scene: Scene;
  private readonly params: WeatherParams;
  private readonly sunDir = { x: 0, y: 1, z: 0 };
  /** sun vector for external shaders (water) */
  get sunVec(): { x: number; y: number; z: number } {
    return this.sunDir;
  }
  get overcast(): number {
    return this.params.overcast;
  }
  get rain(): number {
    return this.params.rain;
  }
  /** world hour 0..24 and world day index (updated each frame) */
  hourOfDay = 12;
  dayNumber = 0;
  private elapsed = 0;
  night = 0;
  dayLight = 1;
  /** latest clock sync from the server (or the local fallback clock) */
  clock: ClockSync = { worldTime: 17 * 60 * 60000 + 5 * 60000, timeScale: 30, serverNow: Date.now() };

  constructor(babylon: BModule, scene: Scene, opts: SkyOptions) {
    this.B = babylon;
    this.scene = scene;
    this.params = { ...WEATHER_PRESETS.clear };

    babylon.Effect.ShadersStore.knSkyVertexShader = SKY_VERT;
    babylon.Effect.ShadersStore.knSkyFragmentShader = SKY_FRAG;

    const sky = babylon.MeshBuilder.CreateSphere(
      'sky',
      { diameter: 5000, segments: 24, sideOrientation: 1 /* BACKSIDE */ },
      scene,
    );
    sky.infiniteDistance = true;
    sky.isPickable = false;
    const mat = new babylon.ShaderMaterial(
      'skyMat',
      scene,
      { vertex: 'knSky', fragment: 'knSky' },
      {
        attributes: ['position'],
        uniforms: [
          'worldViewProjection',
          'sunDir',
          'time',
          'cloud',
          'overcast',
          'flash',
          'fogAmt',
          'fogColor',
          'octaves',
        ],
      },
    );
    mat.backFaceCulling = false;
    mat.disableDepthWrite = true;
    mat.fogEnabled = false;
    sky.material = mat;
    this.skyMesh = sky;
    this.sm = mat;
    this.octaves = opts.cloudOctaves;

    this.sun = new babylon.DirectionalLight('sun', new babylon.Vector3(-0.3, -1, 0.4), scene);
    this.sun.intensity = 2.5;
    this.hemi = new babylon.HemisphericLight('hemi', new babylon.Vector3(0.1, 1, 0.05), scene);
    this.hemi.intensity = 0.6;

    scene.fogMode = 2; // FOGMODE_EXP2
    scene.fogDensity = 0.0009;
    scene.fogColor = new babylon.Color3(0.6, 0.7, 0.8);

    // P1-T09: sky reflection probe for IBL (SPEC-01 §5 "Environment (IBL)").
    // Renders only the sky dome, refresh-once, re-armed when sun height or
    // cloud state visibly changes (prototype recipe).
    const envSize = opts.envSize ?? 64;
    const probe = new babylon.ReflectionProbe('skyProbe', envSize, scene, true);
    probe.renderList?.push(sky);
    probe.refreshRate = babylon.RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
    probe.position.set(0, 0, 0);
    scene.environmentTexture = probe.cubeTexture;
    this.probe = probe;
  }

  private probe: import('babylonjs').ReflectionProbe | null = null;
  private probeKey = '';

  private octaves: number;
  private flash = 0;
  private nextFlash = 6;
  weatherKind: WeatherState = 'clear';
  weatherOverride: WeatherState | null = null;

  /** Advance one frame: clock -> sun -> eased weather -> fog + uniforms + lights. */
  update(dt: number, nowMs: number): void {
    const B = this.B;
    this.elapsed += dt;
    const worldTime = this.clock.worldTime + (nowMs - this.clock.serverNow) * this.clock.timeScale;
    const minutes = dayMinutes(worldTime);
    this.hourOfDay = minutes / 60;
    this.dayNumber = worldDay(worldTime);
    const dir = sunDirection(minutes, worldDay(worldTime) % 365);
    this.sunDir.x = dir[0];
    this.sunDir.y = dir[1];
    this.sunDir.z = dir[2];
    const sunH = this.sunDir.y;
    const dayW = smooth(-0.06, 0.28, sunH);
    this.dayLight = dayW;
    this.night = 1 - smooth(-0.1, 0.1, sunH);

    // weather: auto from the deterministic timeline, or manual override
    const kind = this.weatherOverride ?? weatherAtWorldTime(this.worldSeed, worldTime);
    this.weatherKind = kind;
    const target = WEATHER_PRESETS[kind];
    const k = Math.min(1, dt * 0.35);
    for (const key of ['cloud', 'overcast', 'fog', 'rain', 'snow'] as const) {
      this.params[key] += (target[key] - this.params[key]) * k;
    }
    this.params.storm = target.storm > 0.5 && kind === 'storm' ? 1 : 0;

    // lightning (storm only)
    this.flash *= Math.pow(0.0005, dt);
    if (this.params.storm > 0.5) {
      this.nextFlash -= dt;
      if (this.nextFlash <= 0) {
        this.flash = 1.0;
        this.nextFlash = 3 + Math.random() * 9;
      }
    }

    // fog & horizon (shader-mirrored colours)
    const duskW = clamp01(1 - Math.abs(sunH - 0.02) * 6);
    let hor: RGB = mixC([0.05, 0.065, 0.11], [0.62, 0.76, 0.9], dayW);
    hor = mixC(hor, [0.98, 0.66, 0.48], duskW * 0.32);
    const g = hor[0] * 0.3 + hor[1] * 0.5 + hor[2] * 0.2;
    // overcast desaturates, but keep it a MID grey: lifting toward 0.95 at
    // noon turns the whole far field into a white wall (owner feedback:
    // "why is it so white for everything?"). 0.45/0.62 keeps depth readable.
    hor = mixC(
      hor,
      [g * lerp(0.32, 0.62, dayW), g * lerp(0.34, 0.64, dayW), g * lerp(0.38, 0.68, dayW)],
      this.params.overcast * 0.75,
    );
    this.scene.fogColor.set(hor[0] + this.flash * 0.4, hor[1] + this.flash * 0.4, hor[2] + this.flash * 0.45);
    // rain thickens the air, but its own haze rides on the rain COLOURED
    // fog below, not a bright wash; keep density under control at noon
    this.scene.fogDensity =
      0.00018 +
      this.params.fog * this.params.fog * 0.0036 +
      this.params.fog * 0.0004 +
      this.params.rain * 0.0005;
    this.scene.clearColor.set(hor[0], hor[1], hor[2], 1);

    // sky uniforms
    this.sm.setVector3('sunDir', new B.Vector3(this.sunDir.x, this.sunDir.y, this.sunDir.z));
    this.sm.setFloat('time', this.elapsed);
    this.sm.setFloat('cloud', this.params.cloud);
    this.sm.setFloat('overcast', this.params.overcast);
    this.sm.setFloat('flash', this.flash);
    this.sm.setFloat('fogAmt', this.params.fog);
    this.sm.setColor3('fogColor', this.scene.fogColor);
    this.sm.setFloat('octaves', this.octaves);

    // lights
    const sun = this.sun;
    if (sunH > -0.04) {
      sun.direction.set(-this.sunDir.x, -this.sunDir.y, -this.sunDir.z);
      const low = smooth(0.0, 0.45, sunH);
      sun.diffuse.set(1.0, lerp(0.55, 0.95, low), lerp(0.32, 0.86, low));
      sun.intensity = smooth(-0.04, 0.12, sunH) * 3.0 * (1 - this.params.overcast * 0.72);
    } else {
      sun.direction.set(-this.sunDir.x, -Math.abs(this.sunDir.y) - 0.3, -this.sunDir.z);
      sun.diffuse.set(0.55, 0.65, 0.95);
      sun.intensity = 0.35 * (1 - this.params.overcast * 0.7);
    }
    sun.specular.set(sun.diffuse.r, sun.diffuse.g, sun.diffuse.b);
    // overcast adds fill light, but a +50 % boost washed every facade
    // (near buildings measured at lum ~140); +25 % keeps rooms readable
    this.hemi.intensity = lerp(0.34, 0.62, dayW) * (1 + this.params.overcast * 0.25) + this.flash * 1.5;
    this.hemi.diffuse.set(lerp(0.36, 0.8, dayW), lerp(0.42, 0.86, dayW), lerp(0.68, 0.98, dayW));
    this.hemi.groundColor.set(0.3, lerp(0.22, 0.27, dayW), lerp(0.16, 0.22, dayW));
    this.scene.environmentIntensity = lerp(0.16, 0.72, dayW) * (1 - this.params.overcast * 0.25) + this.flash;

    // re-arm the IBL probe only when the sky state visibly changed
    if (this.probe) {
      const key =
        String(Math.round(sunH * 40)) +
        ':' +
        String(Math.round(this.params.overcast * 8)) +
        ':' +
        String(Math.round(this.params.cloud * 6)) +
        ':' +
        String(this.flash > 0.3 ? 1 : 0);
      if (key !== this.probeKey) {
        this.probeKey = key;
        this.probe.cubeTexture.resetRefreshCounter();
      }
    }
  }

  worldSeed = 1337;

  get weatherParams(): WeatherParams {
    return { ...this.params };
  }

  /** "HH:MM" of game time. */
  timeStr(nowMs: number): string {
    const m = Math.floor(worldMinutes(this.clock, nowMs)) % 1440;
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  dispose(): void {
    this.sm.dispose();
    this.skyMesh.dispose();
    void (this.scene as unknown as { engine?: AbstractEngine });
  }
}
